import { Component, ViewChild } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { ConfirmationDialogComponent } from '@app/components';
import { CompanyMenuService } from '@app/modules/company/services';
import { MenuColumnItem } from '@app/modules/table/interfaces/menu-column-item.interface';
import { AuthService } from '@app/services/auth.service';
import { NotifyService } from '@app/services/notify.service';
import { IndexView } from '@common/views';
import { OfficesIndexTable } from '@company/services/tables/offices-table/offices-index.table';
import { BaseForm } from '@forms/base.form';
import { Company } from '@models/company/company.model';
import { Office } from '@models/company/office.model';
import { TranslateService } from '@ngx-translate/core';

type OfficeShowDialogType = 'ADD_OFFICE';

@Component({
    templateUrl: './office-index.template.html',
    styleUrls: ['./office-index.style.scss'],
})
export class OfficeIndexView extends IndexView {
    @ViewChild('officeForm', { static: true }) officeForm!: BaseForm;
    @ViewChild('confirmMakePrimary', { static: true }) confirmMakePrimary!: ConfirmationDialogComponent;

    isLoading = false;
    _googleImage = '';
    offices: Office[] = [];
    showMap = false;

    currentlyOpenDialog?: OfficeShowDialogType;

    constructor(
        private auth: AuthService,
        private route: ActivatedRoute,
        private notify: NotifyService,
        private router: Router,
        public table: OfficesIndexTable,
        public companyMenuService: CompanyMenuService,
        private translateService: TranslateService
    ) {
        super();
        // Load the company because the primary_id lives on company
        Company.find(this.auth.company.id).then((company) => {
            this.table.setCompany(company);
            this.table.reload();
        });

        // Load offices specifically for google maps
        Office.param('company', this.auth.company.id)
            .get()
            .then(([offices]) => {
                if (offices.length == 0) {
                    this.showMap = false;
                    return;
                }

                this.showMap = true;
                this.offices = offices;
                this.buildGoogleImageUrl();
            });
    }

    menuAction(event: { menuItem: MenuColumnItem; data: Office }): void {
        const { menuItem, data: office } = event;

        if (menuItem.event === 'makePrimary') {
            this.onMakePrimary(office);
        }
    }

    onCreate(office: Office, form: BaseForm): void {
        if (!office.isDirty() || !form.valid) {
            return;
        }

        this.isLoading = true;
        office
            .param('company', this.auth.company.id)
            .save()
            .then((res) => {
                form.reset();
                this.notify.success('company.offices.officeSaveSuccessMessage');
                this.table.reload();
                this.router.navigate([res.id], { relativeTo: this.route });
            })
            .catch((err) => {
                this.notify.error(err.message || 'company.offices.officeSaveErrorMessage');
                this.isLoading = false;
            });
    }

    onMakePrimary(office: Office): void {
        this.confirmMakePrimary.open().then((yes: boolean) => {
            if (!yes) {
                return;
            }
            office.isPrimary = true;
            this.isLoading = true;
            office
                .param('company', this.auth.company.id)
                .save()
                .then(() => {
                    this.notify.success('company.offices.primaryAddressUpdatedMessage');

                    // Load the company because the primary_id lives on company
                    Company.find(this.auth.company.id).then((company) => {
                        this.table.setCompany(company);
                        this.table.reload();
                        this.isLoading = false;
                    });
                })
                .catch((err) => {
                    this.notify.error(err.message || 'company.offices.officeSaveErrorMessage');
                    this.isLoading = false;
                });
        });
    }

    onCancel(): void {
        this.officeForm.reset();
        this.currentlyOpenDialog = undefined;
    }

    private buildGoogleImageUrl(): void {
        let src = 'https://maps.googleapis.com/maps/api/staticmap?';
        for (const office of this.offices) {
            src += `&markers=color:blue%7C${office.addressLine1},${office.city},${office.country}`;
        }
        src += '&zoom=3&scale=2&size=1400x250';
        src += '&maptype=roadmap';
        src += `&language=${this.translateService.currentLang.split('_')[0]}`;
        src += '&key=AIzaSyA54StRlp_plgojz590qzCas8maj5RIkMc';
        this._googleImage = src;
    }
}
